/**
 * RLS Validator - Checks that generated SQL migrations have Row Level Security enabled.
 * DayOne DB requires all tables to have RLS for security.
 */

export interface RLSValidationResult {
  passed: boolean;
  tables: Array<{
    name: string;
    hasRLS: boolean;
    hasPolicy: boolean;
  }>;
  issues: string[];
}

/**
 * Extract table names from CREATE TABLE statements
 */
function extractTableNames(sql: string): string[] {
  const tableRegex = /CREATE\s+TABLE\s+(?:IF\s+NOT\s+EXISTS\s+)?(?:"?(\w+)"?\.)?("?(\w+)"?)/gi;
  const tables: string[] = [];
  let match;

  while ((match = tableRegex.exec(sql)) !== null) {
    // match[3] is the table name (schema is match[1])
    const tableName = match[3] || match[2];
    if (tableName) {
      tables.push(tableName.replace(/"/g, ""));
    }
  }

  return [...new Set(tables)]; // Deduplicate
}

/**
 * Check if a table has ENABLE ROW LEVEL SECURITY
 */
function tableHasRLS(sql: string, tableName: string): boolean {
  // Match: ALTER TABLE [schema.]table ENABLE ROW LEVEL SECURITY
  const rlsRegex = new RegExp(
    `ALTER\\s+TABLE\\s+(?:"?\\w+"?\\.)?(?:"?${tableName}"?)\\s+ENABLE\\s+ROW\\s+LEVEL\\s+SECURITY`,
    "i"
  );
  return rlsRegex.test(sql);
}

/**
 * Check if a table has at least one policy defined
 */
function tableHasPolicy(sql: string, tableName: string): boolean {
  // Match: CREATE POLICY ... ON [schema.]table
  const policyRegex = new RegExp(
    `CREATE\\s+POLICY\\s+.+?\\s+ON\\s+(?:"?\\w+"?\\.)?(?:"?${tableName}"?)`,
    "i"
  );
  return policyRegex.test(sql);
}

/**
 * Validate that all tables in a migration file have RLS enabled.
 * Returns validation result with details for each table.
 */
export function validateRLS(sql: string): RLSValidationResult {
  const tables = extractTableNames(sql);
  const issues: string[] = [];

  const tableResults = tables.map((tableName) => {
    const hasRLS = tableHasRLS(sql, tableName);
    const hasPolicy = tableHasPolicy(sql, tableName);

    if (!hasRLS) {
      issues.push(`Table "${tableName}" is missing ENABLE ROW LEVEL SECURITY`);
    }
    if (!hasPolicy) {
      issues.push(`Table "${tableName}" has no RLS policies defined`);
    }

    return {
      name: tableName,
      hasRLS,
      hasPolicy,
    };
  });

  return {
    passed: issues.length === 0,
    tables: tableResults,
    issues,
  };
}

/**
 * Quick check for common RLS anti-patterns
 */
export function checkRLSAntiPatterns(sql: string): string[] {
  const warnings: string[] = [];

  // Check for tables without user_id column (might not need RLS, but worth flagging)
  const tables = extractTableNames(sql);
  for (const table of tables) {
    // Look for table definition
    const tableDefRegex = new RegExp(
      `CREATE\\s+TABLE[^;]*${table}[^;]*\\([^;]*\\)`,
      "is"
    );
    const match = sql.match(tableDefRegex);
    if (match && !match[0].toLowerCase().includes("user_id")) {
      // Not necessarily an issue - some tables (like public lookups) don't need user_id
      // warnings.push(`Table "${table}" has no user_id column - verify RLS policy is appropriate`);
    }
  }

  // Check for overly permissive policies
  if (/CREATE\s+POLICY.*\s+USING\s*\(\s*true\s*\)/i.test(sql)) {
    warnings.push("Found policy with USING (true) - this allows all access, verify intentional");
  }

  // Check for missing WITH CHECK
  const policyMatches = sql.matchAll(/CREATE\s+POLICY[^;]+FOR\s+(INSERT|UPDATE)/gi);
  for (const match of policyMatches) {
    const policyText = match[0];
    if (!policyText.toLowerCase().includes("with check")) {
      warnings.push(`Found ${match[1]} policy without WITH CHECK clause`);
    }
  }

  return warnings;
}

/**
 * Validate all SQL files in a file set
 */
export function validateMigrationFiles(
  files: Array<{ name: string; content: string }>
): {
  passed: boolean;
  results: Array<{
    file: string;
    validation: RLSValidationResult;
    warnings: string[];
  }>;
} {
  const sqlFiles = files.filter(
    (f) =>
      f.name.endsWith(".sql") &&
      (f.name.includes("migration") || f.name.includes("supabase/"))
  );

  if (sqlFiles.length === 0) {
    return { passed: true, results: [] };
  }

  const results = sqlFiles.map((file) => ({
    file: file.name,
    validation: validateRLS(file.content),
    warnings: checkRLSAntiPatterns(file.content),
  }));

  const passed = results.every((r) => r.validation.passed);

  return { passed, results };
}

export default validateRLS;
